<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :categories="categories"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import colors from '@/utils/colors';
import { mapActions } from 'vuex';
import EventBus from '@/utils/event-bus';
import { OPERATION, ENTITIES, VALUE_TYPES } from '@/services/charts-service';
import { msToTime } from '@/utils/date';
import cardsModel from '@/mixins/cards-model';

export default {
  name: 'MetricTimeSeries',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    metric: {
      type: [String, Array],
      default: null,
    },
    entity: {
      type: String,
      default: ENTITIES.POSTS,
    },
    label: {
      type: String,
      default: null,
    },
    operation: {
      type: String,
      default: OPERATION.SUM,
    },
    valueType: {
      type: String,
      default: null,
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      classifiedPercentage: 0,
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        plotOptions: {
          areaspline: {
            lineColor: colors.componentsColor.seriesLineColor,
            lineWidth: 1,
            fillColor: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, colors.componentsColor.seriesAreaColorStart],
                [1, colors.componentsColor.seriesAreaColorEnd],
              ],
            },
            marker: {
              fillColor: colors.componentsColor.seriesLineColor,
              radius: 2,
            },
          },
        },
        legend: false,
        ...(
          this.valueType === VALUE_TYPES.TIME
            ? {
              yAxis: {
                type: 'datetime',
                labels: {
                  formatter() {
                    return msToTime(this.value);
                  },
                },
              },
              tooltip: {
                pointFormatter() {
                  const { y, color, series } = this;
                  const value = msToTime(y);
                  return `<span style="color:${color}">●</span> ${series.name}: <b>${value}</b><br/>`;
                },
              },
            }
            : null
        ),
      },
    };
  },
  computed: {
    formattedData() {
      return this.rawSeries?.points?.map(({ x, z: y }) => ({ x, y }));
    },
    series() {
      return [{
        data: this.isQuarterOnDatePortion ? this.parseData(this.formattedData) : this.formattedData,
        name: this.label || this.title,
      }];
    },
    categories() {
      if (!this.isQuarterOnDatePortion) {
        return null;
      }

      return this.formattedData?.map(({ x }) => this.formattedLabelForQuarter(x));
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      let fields;

      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (this.metric) {
        fields = [this.metric];
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        points_parser: 'points',
        entity: this.entity,
        params: {
          fields,
          operators: this.operation,
          datePortion: this.getDatePortion(options),
        },
      })
        .then(response => {
          this.rawSeries = response;
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    parseData(data) {
      return this.isQuarterOnDatePortion ? data?.map(({ y }) => y) : data?.data;
    },
  },
};
</script>
